import React, { useState } from 'react';
import { StepProps } from '../../types/onboarding';
import ClientCreationWizard from '../clients/ClientCreationWizard';
import { CreateClientForm, ClientLocationDto } from '../../types';
import { useMutation } from '@tanstack/react-query';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import { useOnboarding } from '../../contexts/OnboardingContext';

export const ClientStep: React.FC<StepProps> = ({ onNext }) => {
  const [showWizard, setShowWizard] = useState(true);
  const { refreshStatus } = useOnboarding();

  const createClientMutation = useMutation({
    mutationFn: async ({ client, location }: { client: CreateClientForm; location: Partial<ClientLocationDto> }) => {
      const clientResponse = await apiService.createClient(client);
      if (!clientResponse.success || !clientResponse.data) {
        throw new Error(clientResponse.message || 'Failed to create client');
      }
      
      const clientId = clientResponse.data.id;
      const locationResponse = await apiService.createClientLocation(clientId, location as ClientLocationDto);
      if (!locationResponse.success) {
        throw new Error(locationResponse.message || 'Failed to create location');
      }
      
      return { client: clientResponse.data, location: locationResponse.data };
    },
    onSuccess: () => {
      toast.success('Client created successfully!');
      refreshStatus();
      setTimeout(() => {
        setShowWizard(false);
        onNext();
      }, 1500);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create client');
    },
  });

  const handleClientCreated = (clientData: CreateClientForm, locationData: Partial<ClientLocationDto>) => {
    createClientMutation.mutate({ client: clientData, location: locationData });
  };

  return (
    <div>
      <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Add Your First Client
      </h3>
      <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
        Clients are the businesses or organizations you provide services to.
        You'll also create their primary location where work will be performed.
      </p>

      {showWizard && (
        <ClientCreationWizard
          onSubmit={handleClientCreated}
          isLoading={createClientMutation.isPending}
          error={createClientMutation.error?.message}
          onCancel={() => {}} // Don't allow canceling in onboarding
        />
      )}
    </div>
  );
};

